import React, { useState, useEffect } from 'react';
import { blobticZoomService } from '../../Commun/services/blobticZoomService';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    IconButton,
    Typography,
    Alert,
    Stack,
    Tooltip
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Add as AddIcon,
    Business as BusinessIcon,
    Edit as EditIcon
} from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { ZoomField } from '../../Commun/components/ZoomField';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';

interface User {
    USR_IDF: string;
    USR_CODE: string;
    USR_LIB: string;
}

interface Dossier {
    USRDOS_IDF?: string;
    DOS_IDF: string;
    DOS_CODE: string;
    DOS_LIB: string;
    PRF_IDF?: string;
    PRF_CODE?: string;
    PRF_LIB?: string;
    USRDOS_START_DATE?: string;
    USRDOS_END_DATE?: string;
}

const UserDossierPage: React.FC = () => {
    const { t } = useLanguage();
    

    // Selection state
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [assignedDossiers, setAssignedDossiers] = useState<Dossier[]>([]);
    const [loading, setLoading] = useState(false);

    // Dialog state
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null); // Track editing ID
    const [newDossier, setNewDossier] = useState<any>(null); // From Zoom
    const [newProfile, setNewProfile] = useState<any>(null); // From Zoom
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [dialogError, setDialogError] = useState<string | null>(null);

    // Confirm Delete state
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

    // Load dossiers when user changes
    useEffect(() => {
        if (selectedUser?.USR_IDF) {
            loadAssignedDossiers(selectedUser.USR_IDF);
        } else {
            setAssignedDossiers([]);
        }
    }, [selectedUser]);

    const loadAssignedDossiers = async (userId: string) => {
        setLoading(true);
        try {
            const response = await blobticZoomService.executeRawAction(
                'Blobtic_Parameters,sys_userdossier,select',
                { USR_IDF: userId }
            );

            if (response.success) {
                setAssignedDossiers(response.data as Dossier[]);
            } else {
                blobticMessage.error(t('common.messages.error'), response.message || response.error || t('common.error.loading'));
            }
        } catch (error) {
            console.error(error);
            blobticMessage.error(t('common.messages.error'), t('common.messages.comm_error'));
        } finally {
            setLoading(false);
        }
    };

    const handleOpenAddDialog = () => {
        setEditingId(null);
        setNewDossier(null);
        setNewProfile(null);
        setStartDate(new Date().toISOString().split('T')[0]); // Default Today
        setEndDate('');
        setDialogError(null);
        setOpenAddDialog(true);
    };

    const handleEditClick = (dossier: Dossier) => {
        setEditingId(dossier.USRDOS_IDF || null);

        // Pre-fill Dossier
        setNewDossier({
            DOS_IDF: dossier.DOS_IDF,
            DOS_CODE: dossier.DOS_CODE,
            DOS_LIB: dossier.DOS_LIB
        });

        // Pre-fill Profile if exists
        if (dossier.PRF_IDF) {
            setNewProfile({
                PRF_IDF: dossier.PRF_IDF,
                PRF_CODE: dossier.PRF_CODE,
                PRF_LIB: dossier.PRF_LIB
            });
        } else {
            setNewProfile(null);
        }

        // Dates
        setStartDate(dossier.USRDOS_START_DATE ? dossier.USRDOS_START_DATE.split('T')[0] : '');
        setEndDate(dossier.USRDOS_END_DATE ? dossier.USRDOS_END_DATE.split('T')[0] : '');

        setDialogError(null);
        setOpenAddDialog(true);
    };

    const handleAddDossier = async () => {
        if (!selectedUser || !newDossier) {
            setDialogError(t('zoom.check.fillRequired'));
            return;
        }

        try {
            const params: any = {
                USR_IDF: selectedUser.USR_IDF,
                DOS_IDF: newDossier.DOS_IDF,
                USRDOS_START_DATE: startDate,
            };

            if (newProfile?.PRF_IDF) {
                params.PRF_IDF = newProfile.PRF_IDF;
            }
            if (endDate) {
                params.USRDOS_END_DATE = endDate;
            }

            let actionString = 'Blobtic_Parameters,sys_userdossier,insert';

            // If editing, add ID and switch to update
            if (editingId) {
                params.USRDOS_IDF = editingId;
                actionString = 'Blobtic_Parameters,sys_userdossier,update';
            }

            const response = await blobticZoomService.executeRawAction(
                actionString,
                params
            );

            if (response.success) {
                blobticMessage.success(t('common.messages.success'), t('common.messages.saved'));
                setOpenAddDialog(false);

                if (editingId && Array.isArray(response.data) && response.data.length > 0) {
                    // Optimized refresh for Update
                    const updatedItem = response.data[0] as Dossier;
                    setAssignedDossiers(prev => prev.map(item =>
                        item.USRDOS_IDF === updatedItem.USRDOS_IDF ? updatedItem : item
                    ));
                } else {
                    // Full reload for Insert or if data missing
                    loadAssignedDossiers(selectedUser.USR_IDF);
                }
            } else {
                setDialogError(response.message || response.error || t('common.messages.save_error'));
            }
        } catch (error) {
            console.error(error);
            setDialogError(t('common.messages.comm_error'));
        }
    };

    const handleDeleteClick = (userDosId: string) => {
        setConfirmDeleteId(userDosId);
    };

    const handleConfirmDelete = async () => {
        if (!confirmDeleteId) return;

        const userDosId = confirmDeleteId;
        setConfirmDeleteId(null);

        try {
            const response = await blobticZoomService.executeRawAction(
                'Blobtic_Parameters,sys_userdossier,delete',
                { USRDOS_IDF: userDosId }
            );

            if (response.success) {
                blobticMessage.success(t('common.messages.success'), t('common.messages.deleted_success'));
                if (selectedUser?.USR_IDF) {
                    loadAssignedDossiers(selectedUser.USR_IDF);
                }
            } else {
                blobticMessage.error(t('common.messages.error'), response.message || response.error || t('common.messages.delete_error'));
            }
        } catch (error) {
            console.error(error);
            blobticMessage.error(t('common.messages.error'), t('common.messages.comm_error'));
        }
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString();
    };

    // Actions for GenericPage header
    const pageActions = (
        <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            disabled={!selectedUser} // Disable if no user selected
        >
            {t('common.actions.new')}
        </Button>
    );

    return (
        <GenericPage
            title={t('menu.planning.admin.userDossiers')}
            icon={<BusinessIcon />}
            additionalActions={pageActions}
            footerEnabled={false}
        >
            <Box sx={{ maxWidth: 1000, margin: '0 auto' }}>
                {/* User Selection */}
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {t('page.users.zoom.label')}
                    </Typography>
                    <ZoomField
                        zoomKey="user"
                        label={t('common.labels.user')}
                        placeholder={t('page.users.zoom.helper')}
                        displayField="USR_LIB"
                        valueField="USR_IDF"
                        value={selectedUser?.USR_LIB || ''}
                        onChange={(val, data) => {
                            if (data) {
                                setSelectedUser({
                                    USR_IDF: data.USR_IDF,
                                    USR_CODE: data.USR_CODE,
                                    USR_LIB: data.USR_LIB
                                });
                            } else {
                                setSelectedUser(null);
                            }
                        }}
                        showSelectedInfo
                        fullWidth
                    />
                </Paper>

                {/* Assigned Dossiers List */}
                {selectedUser && (
                    <Paper sx={{ p: 2 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">
                                {t('zoom.title.default', 'Dossiers')}
                            </Typography>
                            {/* Button moved to header */}
                        </Stack>

                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                                        <TableCell>Code</TableCell>
                                        <TableCell>Libellé</TableCell>
                                        <TableCell>{t('common.profile')}</TableCell>
                                        <TableCell>{t('page.users.field.start_date')}</TableCell>
                                        <TableCell>{t('page.users.field.end_date')}</TableCell>
                                        <TableCell align="center" width={120}>{t('page.parameters.table.actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {assignedDossiers.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                                <Typography color="text.secondary">
                                                    {t('zoom.empty.data')}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        assignedDossiers.map((row) => (
                                            <TableRow key={row.USRDOS_IDF}>
                                                <TableCell>{row.DOS_CODE}</TableCell>
                                                <TableCell>{row.DOS_LIB}</TableCell>
                                                <TableCell>{row.PRF_LIB || '-'}</TableCell>
                                                <TableCell>{formatDate(row.USRDOS_START_DATE)}</TableCell>
                                                <TableCell>{formatDate(row.USRDOS_END_DATE)}</TableCell>
                                                <TableCell align="center">
                                                    <Stack direction="row" spacing={1} justifyContent="center">
                                                        <Tooltip title={t('common.actions.edit')}>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleEditClick(row)}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => row.USRDOS_IDF && handleDeleteClick(row.USRDOS_IDF)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}

                {/* Add Dialog - Using RootDialogPage */}
                <RootDialogPage
                    open={openAddDialog}
                    onClose={() => setOpenAddDialog(false)}
                    title={editingId ? t('common.actions.edit') : t('common.actions.new')}
                    icon={editingId ? <EditIcon /> : <AddIcon />}
                    onAction={handleAddDossier}
                    actionLabel={t('common.actions.save')}
                >
                    {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}

                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <ZoomField
                                zoomKey="agency"
                                label={t('login.agency')}
                                required
                                value={newDossier?.DOS_CODE || ''}
                                onChange={(val, data) => setNewDossier(data)}
                                displayField="DOS_CODE"
                                valueField="DOS_IDF"
                                showSelectedInfo={false}
                                fullWidth
                            />

                            <ZoomField
                                zoomKey="profil"
                                label={t('common.profile')}
                                value={newProfile?.PRF_LIB || ''}
                                onChange={(val, data) => setNewProfile(data)}
                                displayField="PRF_LIB"
                                valueField="PRF_IDF"
                                showSelectedInfo
                                fullWidth
                            />
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                label={t('page.users.field.start_date')}
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                            <TextField
                                label={t('page.users.field.end_date')}
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Stack>
                    </Box>
                </RootDialogPage>

                {/* Confirm Delete Dialog */}
                <Dialog
                    open={!!confirmDeleteId}
                    onClose={() => setConfirmDeleteId(null)}
                >
                    <DialogTitle>{t('common.confirm.title.delete')}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {t('common.confirm.delete')}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmDeleteId(null)}>{t('btn.cancel')}</Button>
                        <Button onClick={handleConfirmDelete} color="error" autoFocus>
                            {t('common.actions.confirm')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </GenericPage>
    );
};

export default UserDossierPage;
